import { Controller } from '@hotwired/stimulus';
import Swiper from 'swiper';
import { A11y, Keyboard } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { dispatchEvent, subscribeEvent, unsubscribeEvent } from '@shared/script/eventDispatcher';
import {
    calculateVisibleSlides,
    calculateTotalPages,
    calculateCurrentPage
} from '../../module/carousel/utilities';

type BreakpointSettings = {
  slidesPerView: number;
  spaceBetween?: number;
};

type BreakpointsConfig = Record<number, BreakpointSettings>;

export default class CarouselController extends Controller<HTMLElement> {
    static values = {
        id: String,
        centered: { type: Boolean, default: true },
        numberVisible: Number,
        roundArrows: Boolean,
        autoHeight: { type: Boolean, default: false },
        loop: { type: Boolean, default: true },
        breakpoints: Object as ValueDefinition<this, BreakpointsConfig>
    };

    static targets = ['nav', 'counter', 'current', 'total', 'leftArrow', 'rightArrow'];

    declare readonly centeredValue: boolean;
    declare readonly numberVisibleValue: number;
    declare readonly roundArrowsValue: boolean;
    declare readonly navTarget: HTMLDivElement;
    declare readonly counterTarget: HTMLElement;
    declare readonly hasCounterTarget: boolean;
    declare readonly currentTarget: HTMLElement;
    declare readonly totalTarget: HTMLElement;
    declare readonly autoHeightValue: boolean;
    declare readonly loopValue: boolean;
    declare readonly breakpointsValue: BreakpointsConfig;
    declare readonly hasBreakpointsValue: boolean;
    declare readonly idValue: string;
    declare readonly carouselIdValue: string;
    declare readonly leftArrowTarget: HTMLElement;
    declare readonly rightArrowTarget: HTMLElement;

    protected swiper: Swiper | null = null;
    protected carouselElement: HTMLElement | null = null;

    connect(): void {
        this.initializeSwiper();

        subscribeEvent(`${this.idValue}:next`, this.handleNext);
        subscribeEvent(`${this.idValue}:prev`, this.handlePrev);
    }

    private async initializeSwiper(): void {
        const options: SwiperOptions = {
            modules: [A11y, Keyboard],
            loop: this.loopValue,
            slidesPerView: this.numberVisibleValue === 1 ? 'auto' : this.numberVisibleValue,
            breakpoints: this.hasBreakpointsValue ? this.breakpointsValue : undefined,
            centeredSlides: this.centeredValue,
            a11y: {
                enabled: true,
                scrollOnFocus: !this.loopValue
            },
            watchSlidesProgress: true,
            on: {
                init: () => this.updateCounter(),
                transitionEnd: () => {
                    requestAnimationFrame(() => this.updateCounter());
                },
            },
            init: false,
            autoHeight: this.autoHeightValue,
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            }
        };

        this.carouselElement = this.element.querySelector('.swiper');

        this.swiper = new Swiper(this.carouselElement, options);
        this.swiper.on('breakpoint', (swiper: Swiper) => {
            this.updateNavigation(swiper);
            this.updateCounter();
        });
        this.swiper.on('resize', (swiper: Swiper) => {
            this.updateNavigation(swiper);
            this.updateCounter();
        });
        this.swiper.on('slideChange', (swiper: Swiper) => {
            this.updateNavigation(swiper);
        });

        await new Promise(resolve => {
            requestAnimationFrame(resolve);
        });
        this.swiper.init();
    }

    updateNavigation(swiper: Swiper): void {
        if (!swiper.params.loop) {

            this.navTarget?.classList.toggle('carousel__nav--hidden', swiper.isBeginning && swiper.isEnd);

            if (this.hasLeftArrowTarget === true) {
                this.leftArrowTarget.classList.toggle('carousel__arrow--disabled', swiper.isBeginning);
                this.leftArrowTarget.setAttribute('aria-disabled', String(swiper.isBeginning));
                this.leftArrowTarget.setAttribute('tabindex', String(swiper.isBeginning ? -1 : 0));
            }

            if (this.hasRightArrowTarget === true) {
                this.rightArrowTarget.classList.toggle('carousel__arrow--disabled', swiper.isEnd);
                this.rightArrowTarget.setAttribute('aria-disabled', String(swiper.isEnd));
                this.rightArrowTarget.setAttribute('tabindex', String(swiper.isEnd ? -1 : 0));
            }
        }
    }

    updateCounter(): void {
        if (!this.swiper || !this.hasCounterTarget) {
            return;
        }

        const allSlides = Array.from(this.swiper.slides);
        const realSlides = allSlides.filter(slide => !slide.classList.contains('swiper-slide-duplicate'));
        const totalSlides = realSlides.length;

        const swiperWidth = this.swiper?.el?.clientWidth ?? window.innerWidth;
        const visible = calculateVisibleSlides(this.swiper.params, swiperWidth);
        const totalPages = calculateTotalPages(totalSlides, visible);

        const currentPage = calculateCurrentPage(this.swiper.progress, totalPages);

        // On multi breakpoint carousels, hide the nav if there's only one page
        // We only know this after updateCounter is first invoked
        if (totalPages <= 1) {
            this.navTarget?.classList.add('carousel__nav--hidden');
        } else {
            this.navTarget?.classList.remove('carousel__nav--hidden');

            this.currentTarget.textContent = currentPage.toString();
            this.totalTarget.textContent = totalPages.toString();
        }
    }

    goPrev() : void {
        dispatchEvent(`${this.idValue}:prev`);
    }

    goNext(): void {
        dispatchEvent(`${this.idValue}:next`);
    }

    private handleNext = (): void => {
        if (!this.swiper || this.rightArrowTarget?.classList.contains('carousel__arrow--disabled')
        ) {
            return;
        }
        const nextIndex = (this.swiper.realIndex + 1) % this.swiper.slides.length;
        this.swiper.slideToLoop(nextIndex);
    };

    private handlePrev = (): void => {
        if (!this.swiper || this.leftArrowTarget?.classList.contains('carousel__arrow--disabled')) {
            return;
        }
        const total = this.swiper.slides.length;
        const prevIndex = (this.swiper.realIndex - 1 + total) % total;
        this.swiper.slideToLoop(prevIndex);
    };

    disconnect(): void {
        if (this.swiper) {
            this.swiper.destroy();
            this.swiper = null;
        }

        unsubscribeEvent(`${this.idValue}:next`, this.handleNext);
        unsubscribeEvent(`${this.idValue}:prev`, this.handlePrev);
    }
}
